import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Field, Label } from './fieldset';
import { FolderOpen, CheckCircle, XCircle, AlertCircle, RefreshCw, RotateCcw, Plus, Trash2, Info, Link2, ChevronDown, ChevronUp } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { useI18n } from '../hooks/useI18n';
import GoogleDriveConnect from './GoogleDriveConnect';
import GoogleDrivePicker from './GoogleDrivePicker';

interface FolderCategoryRule {
    folder_names: string[];
    folder_keywords: string[];
    folder_ids: string[];
}

interface FolderCategoryRules {
    [category: string]: FolderCategoryRule;
}

interface GoogleDriveCredentials {
    organization_id: string;
    cost_center_id: string;
    folder_id: string;
    folder_name?: string;
    drive_id?: string;
    is_shared_folder: boolean;
    processing_priority: string;
    sync_enabled: boolean;
    sync_frequency_hours: string;
    is_active: boolean;
    last_sync_at?: string;
    created_at: string;
    folder_category_rules?: FolderCategoryRules;
    auth_type?: string;
    oauth_connected?: boolean;
    oauth_email?: string;
}

interface GoogleDriveFormData {
    folder_id: string;
    folder_name?: string;
    drive_id?: string;
    is_shared_folder: boolean;
    processing_priority: string;
    sync_enabled: boolean;
    sync_frequency_hours: string;
    is_active: boolean;
    service_account_json?: string;
    folder_category_rules?: FolderCategoryRules;
    auth_type?: string;
}

interface GoogleDriveFormProps {
    costCenterId: string;
    onCredentialsChange?: (hasCredentials: boolean) => void;
}

export default function GoogleDriveForm({ costCenterId, onCredentialsChange }: GoogleDriveFormProps) {
    const { t } = useI18n();
    const [credentials, setCredentials] = useState<GoogleDriveCredentials | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [syncing, setSyncing] = useState<boolean>(false);
    const [showForm, setShowFormState] = useState<boolean>(false);
    const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
    const [showAdvancedSettings, setShowAdvancedSettings] = useState<boolean>(false);
    const [oauthConnected, setOauthConnected] = useState<boolean>(false);

    const setShowForm = (value: boolean) => {
        setShowFormState(value);
    };
    const [syncResult, setSyncResult] = useState<{ success: boolean; message: string } | null>(null);

    const [formData, setFormData] = useState<GoogleDriveFormData>({
        folder_id: '',
        folder_name: '',
        drive_id: '',
        is_shared_folder: false,
        processing_priority: '5',
        sync_enabled: true,
        sync_frequency_hours: '24',
        is_active: true,
        service_account_json: '',
        folder_category_rules: {},
        auth_type: 'oauth'
    });

    // Local state for raw input strings (to allow typing commas)
    const [rawInputs, setRawInputs] = useState({
        bankFolderNames: '',
        bankFolderKeywords: '',
        bankFolderIds: '',
        paymentFolderNames: '',
        paymentFolderKeywords: '',
        paymentFolderIds: ''
    });

    // Organization-level OAuth connection (separate from cost center credentials)
    const [orgOAuthConnection, setOrgOAuthConnection] = useState<{ google_email: string } | null>(null);

    // Load org-level OAuth connection
    const loadOAuthConnection = async () => {
        try {
            const response = await apiFetch('/api/gdrive-oauth/connection');
            if (response.ok) {
                const data = await response.json();
                setOrgOAuthConnection(data);
                setOauthConnected(!!data);
            } else {
                setOrgOAuthConnection(null);
                setOauthConnected(false);
            }
        } catch (error) {
            console.error('Error loading OAuth connection:', error);
        }
    };

    // Load existing credentials
    const loadCredentials = async () => {
        if (!costCenterId) return;

        try {
            setLoading(true);
            
            // Also load org OAuth connection
            await loadOAuthConnection();
            
            const response = await apiFetch(`/api/gdrive-invoices/credentials/${costCenterId}`);

            if (response.ok) {
                const data = await response.json();
                setCredentials(data);
                setFormData({
                    folder_id: data.folder_id,
                    folder_name: data.folder_name || '',
                    is_shared_folder: data.is_shared_folder,
                    processing_priority: data.processing_priority,
                    sync_enabled: data.sync_enabled,
                    sync_frequency_hours: data.sync_frequency_hours,
                    is_active: data.is_active,
                    folder_category_rules: data.folder_category_rules || {}
                });
                // Initialize raw inputs from loaded data
                const rules = data.folder_category_rules || {};
                setRawInputs({
                    bankFolderNames: rules.bank_statement?.folder_names?.join(', ') || '',
                    bankFolderKeywords: rules.bank_statement?.folder_keywords?.join(', ') || '',
                    bankFolderIds: rules.bank_statement?.folder_ids?.join(', ') || '',
                    paymentFolderNames: rules.payment_report?.folder_names?.join(', ') || '',
                    paymentFolderKeywords: rules.payment_report?.folder_keywords?.join(', ') || '',
                    paymentFolderIds: rules.payment_report?.folder_ids?.join(', ') || ''
                });
                onCredentialsChange?.(true);
            } else if (response.status === 404) {
                setCredentials(null);
                onCredentialsChange?.(false);
            } else {
                console.error('Failed to load Google Drive credentials, status:', response.status);
            }
        } catch (error) {
            console.error('Error loading Google Drive credentials:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        console.log('GoogleDriveForm: costCenterId changed to:', costCenterId);
        loadCredentials();
    }, [costCenterId]);

    useEffect(() => {
        console.log('GoogleDriveForm: component mounted');
        return () => {
            console.log('GoogleDriveForm: component unmounting');
        };
    }, []);

    // Save credentials
    const handleSave = async () => {
        if (!costCenterId) return;

        try {
            setSaving(true);
            setSyncResult(null);

            const payload = {
                ...formData,
                cost_center_id: costCenterId
            };

            const url = credentials
                ? `/api/gdrive-invoices/credentials/${costCenterId}`
                : '/api/gdrive-invoices/credentials';

            const method = credentials ? 'PUT' : 'POST';

            const response = await apiFetch(url, {
                method,
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                await loadCredentials();
                setShowForm(false);
            } else {
                const errorData = await response.json();
                alert(errorData.detail || 'Failed to save Google Drive configuration');
            }
        } catch (error) {
            console.error('Error saving Google Drive configuration:', error);
            alert('Failed to save Google Drive configuration');
        } finally {
            setSaving(false);
        }
    };

    // Trigger sync
    const handleSync = async () => {
        if (!costCenterId || !credentials) return;

        try {
            setSyncing(true);
            setSyncResult(null);

            const response = await apiFetch('/api/gdrive-invoices/sync', {
                method: 'POST',
                body: JSON.stringify({ cost_center_id: costCenterId }),
            });

            if (response.ok) {
                const result = await response.json();
                setSyncResult({
                    success: true,
                    message: `Sync completed: ${result.processed_files || 0} files processed`
                });
                await loadCredentials(); // Refresh to get updated last_sync_at
            } else {
                const errorData = await response.json();
                setSyncResult({
                    success: false,
                    message: errorData.detail || 'Sync failed'
                });
            }
        } catch (error) {
            console.error('Error syncing Google Drive:', error);
            setSyncResult({
                success: false,
                message: 'Sync failed due to network error'
            });
        } finally {
            setSyncing(false);
        }
    };

    // Delete credentials
    const handleDelete = async () => {
        if (!costCenterId || !credentials) return;

        if (!confirm('Are you sure you want to delete the Google Drive configuration? This will disable Google Drive integration for this cost center.')) {
            return;
        }

        try {
            const response = await apiFetch(`/api/gdrive-invoices/credentials/${costCenterId}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                setCredentials(null);
                setSyncResult(null);
                onCredentialsChange?.(false);
            } else {
                const errorData = await response.json();
                alert(errorData.detail || 'Failed to delete Google Drive configuration');
            }
        } catch (error) {
            console.error('Error deleting Google Drive configuration:', error);
            alert('Failed to delete Google Drive configuration');
        }
    };

    if (loading) {
        return (
            <div className="space-y-3">
                <div className="flex items-center space-x-2">
                    <FolderOpen className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{t('gdrive.integration', 'common')}</span>
                </div>
                <div className="text-sm text-muted-foreground">{t('gdrive.loading', 'common')}</div>
            </div>
        );
    }

    return (
        <div className="space-y-4 border-t pt-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <FolderOpen className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{t('gdrive.integration', 'common')}</span>
                    {credentials && (
                        <Badge variant={credentials.is_active ? 'default' : 'secondary'}>
                            {credentials.is_active ? t('status.active', 'common') : 'Inactive'}
                        </Badge>
                    )}
                    {credentials?.is_shared_folder && (
                        <Badge variant="outline">{t('gdrive.sharedFolder', 'common')}</Badge>
                    )}
                </div>

                {credentials ? (
                    <div className="flex space-x-2">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleSync}
                            disabled={syncing}
                        >
                            {syncing ? (
                                <RotateCcw className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                                <RefreshCw className="h-4 w-4 mr-1" />
                            )}
                            {syncing ? t('gdrive.syncing', 'common') : t('gdrive.syncNow', 'common')}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowForm(!showForm)}
                        >
                            {showForm ? t('actions.cancel', 'common') : t('actions.edit', 'common')}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleDelete}
                            className="text-destructive hover:text-destructive/90"
                        >
                            {t('actions.delete', 'common')}
                        </Button>
                    </div>
                ) : orgOAuthConnection ? (
                    // OAuth connected but no credentials for this cost center yet
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowForm(!showForm)}
                    >
                        {showForm ? t('actions.cancel', 'common') : t('gdrive.selectFolder', 'common')}
                    </Button>
                ) : (
                    <Button
                        size="sm"
                        onClick={() => setShowForm(true)}
                    >
                        {t('gdrive.addDrive', 'common')}
                    </Button>
                )}
            </div>

            {/* Show OAuth connected state with folder picker when no credentials exist yet */}
            {!credentials && orgOAuthConnection && !showForm && (
                <div className="space-y-3">
                    {/* OAuth Connection Status */}
                    <div className="flex items-center gap-2 text-sm p-2 bg-green-50 dark:bg-green-950/20 rounded border border-green-200 dark:border-green-800">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span>{t('gdrive.connectedAs', 'common')} <strong>{orgOAuthConnection.google_email}</strong></span>
                    </div>

                    {/* Folder Selection Prompt */}
                    <div className="p-3 border rounded-lg bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800">
                        <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-200 mb-2">
                            <AlertCircle className="h-4 w-4" />
                            <span>{t('gdrive.noFolderSelected', 'common')}</span>
                        </div>
                        <GoogleDrivePicker 
                            onFolderSelected={async (selection) => {
                                // Create new credentials with the selected folder
                                try {
                                    const response = await apiFetch('/api/gdrive-invoices/credentials', {
                                        method: 'POST',
                                        body: JSON.stringify({
                                            cost_center_id: costCenterId,
                                            folder_id: selection.folderId,
                                            folder_name: selection.folderName,
                                            drive_id: selection.driveId || null,
                                            auth_type: 'oauth'
                                        }),
                                    });
                                    if (response.ok) {
                                        await loadCredentials();
                                        onCredentialsChange?.(true);
                                    } else {
                                        const error = await response.json();
                                        console.error('Error creating credentials:', error);
                                    }
                                } catch (error) {
                                    console.error('Error saving folder:', error);
                                }
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Status Information */}
            {credentials && !showForm && (
                <div className="space-y-3">
                    {/* OAuth Connection Status */}
                    {credentials.oauth_connected && (
                        <div className="flex items-center gap-2 text-sm p-2 bg-green-50 dark:bg-green-950/20 rounded border border-green-200 dark:border-green-800">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span>{t('gdrive.connectedAs', 'common')} <strong>{credentials.oauth_email}</strong></span>
                        </div>
                    )}

                    {/* Quick Folder Selection - show if connected but no folder selected */}
                    {credentials.oauth_connected && !credentials.folder_id && (
                        <div className="p-3 border rounded-lg bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800">
                            <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-200 mb-2">
                                <AlertCircle className="h-4 w-4" />
                                <span>{t('gdrive.noFolderSelected', 'common')}</span>
                            </div>
                            <GoogleDrivePicker 
                                onFolderSelected={async (selection) => {
                                    // Save the folder selection immediately
                                    try {
                                        const response = await apiFetch(`/api/gdrive-invoices/credentials/${costCenterId}`, {
                                            method: 'PUT',
                                            body: JSON.stringify({
                                                folder_id: selection.folderId,
                                                folder_name: selection.folderName,
                                                drive_id: selection.driveId || null
                                            }),
                                        });
                                        if (response.ok) {
                                            await loadCredentials();
                                        }
                                    } catch (error) {
                                        console.error('Error saving folder:', error);
                                    }
                                }}
                            />
                        </div>
                    )}

                    {/* Folder Info - show if folder is selected */}
                    {credentials.folder_id && (
                        <div className="p-3 border rounded-lg bg-muted/10">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <FolderOpen className="h-4 w-4 text-green-600" />
                                        <span className="font-medium">{credentials.folder_name || 'Selected folder'}</span>
                                        {credentials.drive_id && <Badge variant="outline" className="text-xs">Shared Drive</Badge>}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">ID: {credentials.folder_id}</div>
                                </div>
                                {credentials.oauth_connected && (
                                    <GoogleDrivePicker 
                                        onFolderSelected={async (selection) => {
                                            try {
                                                const response = await apiFetch(`/api/gdrive-invoices/credentials/${costCenterId}`, {
                                                    method: 'PUT',
                                                    body: JSON.stringify({
                                                        folder_id: selection.folderId,
                                                        folder_name: selection.folderName,
                                                        drive_id: selection.driveId || null
                                                    }),
                                                });
                                                if (response.ok) {
                                                    await loadCredentials();
                                                }
                                            } catch (error) {
                                                console.error('Error saving folder:', error);
                                            }
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                    
                    <div className="text-sm text-muted-foreground space-y-1">
                        <div>Processing Priority: {credentials.processing_priority}</div>
                        <div>Sync Frequency: {credentials.sync_frequency_hours} hours</div>
                        {credentials.last_sync_at && (
                            <div>{t('gdrive.lastSync', 'common')} {new Date(credentials.last_sync_at).toLocaleString()}</div>
                        )}
                        <div>Created: {new Date(credentials.created_at).toLocaleString()}</div>
                        {credentials.auth_type && (
                            <div>Auth Type: <Badge variant="outline" className="text-xs">{credentials.auth_type}</Badge></div>
                        )}
                    </div>

                    {/* Display Folder Category Rules */}
                    {credentials.folder_category_rules && Object.keys(credentials.folder_category_rules).length > 0 && (
                        <div className="pt-2 border-t">
                            <div className="text-sm font-medium mb-2">File Categorization Rules:</div>
                            <div className="space-y-2">
                                {Object.entries(credentials.folder_category_rules).map(([category, rules]) => (
                                    <div key={category} className="text-xs">
                                        <Badge variant="outline" className="text-xs mb-1">{category}</Badge>
                                        {rules.folder_names && rules.folder_names.length > 0 && (
                                            <div className="text-muted-foreground ml-2">
                                                Names: {rules.folder_names.join(', ')}
                                            </div>
                                        )}
                                        {rules.folder_keywords && rules.folder_keywords.length > 0 && (
                                            <div className="text-muted-foreground ml-2">
                                                Keywords: {rules.folder_keywords.join(', ')}
                                            </div>
                                        )}
                                        {rules.folder_ids && rules.folder_ids.length > 0 && (
                                            <div className="text-muted-foreground ml-2">
                                                IDs: {rules.folder_ids.join(', ')}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Sync Result */}
            {syncResult && (
                <div className={`flex items-center space-x-2 text-sm p-2 rounded ${syncResult.success
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                    {syncResult.success ? (
                        <CheckCircle className="h-4 w-4" />
                    ) : (
                        <XCircle className="h-4 w-4" />
                    )}
                    <span>{syncResult.message}</span>
                </div>
            )}

            {/* Configuration Form */}
            {showForm && (
                <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <AlertCircle className="h-4 w-4" />
                        <span>Configure Google Drive folder for automatic invoice processing</span>
                    </div>

                    {/* OAuth Connection Section */}
                    <div className="space-y-3 p-3 border rounded-lg bg-muted/10">
                        <div className="text-sm font-medium flex items-center gap-2">
                            <Link2 className="h-4 w-4" />
                            {t('gdrive.connectionTitle', 'common')}
                        </div>
                        <GoogleDriveConnect 
                            costCenterId={costCenterId} 
                            returnTo={`/cost-centers/${costCenterId}?tab=gdrive`}
                            onConnectionChange={setOauthConnected}
                            compact={true}
                        />
                    </div>

                    {/* Folder Selection Section */}
                    <div className="space-y-3 p-3 border rounded-lg bg-muted/10">
                        <div className="text-sm font-medium flex items-center gap-2">
                            <FolderOpen className="h-4 w-4" />
                            {t('gdrive.folderSelection', 'common')}
                        </div>
                        
                        {/* Google Picker Button */}
                        {oauthConnected && (
                            <GoogleDrivePicker 
                                onFolderSelected={(selection) => {
                                    setFormData({
                                        ...formData,
                                        folder_id: selection.folderId,
                                        folder_name: selection.folderName,
                                        drive_id: selection.driveId || ''
                                    });
                                }}
                            />
                        )}

                        {/* Selected folder info */}
                        {formData.folder_id && (
                            <div className="text-sm p-2 bg-green-50 dark:bg-green-950/20 rounded border border-green-200 dark:border-green-800">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                    <span className="font-medium">{formData.folder_name || 'Selected folder'}</span>
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    ID: {formData.folder_id}
                                    {formData.drive_id && <span> (Shared Drive)</span>}
                                </div>
                            </div>
                        )}

                        {/* Manual folder ID input (fallback) */}
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            className="text-xs"
                        >
                            {showAdvanced ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
                            {showAdvanced ? 'Hide manual entry' : 'Enter folder ID manually'}
                        </Button>

                        {showAdvanced && (
                            <div className="space-y-3 pt-2 border-t">
                                <Field className="">
                                    <Label htmlFor="folder-id" className="">{t('gdrive.folderId', 'common')}</Label>
                                    <Input
                                        id="folder-id"
                                        type="text"
                                        value={formData.folder_id}
                                        onChange={(e) => setFormData({ ...formData, folder_id: e.target.value })}
                                        placeholder="e.g., 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                                    />
                                    <div className="text-xs text-muted-foreground mt-1">
                                        You can find the folder ID in the Google Drive URL when viewing the folder
                                    </div>
                                </Field>

                                <Field className="">
                                    <Label htmlFor="folder-name" className="">{t('gdrive.folderName', 'common')}</Label>
                                    <Input
                                        id="folder-name"
                                        type="text"
                                        value={formData.folder_name || ''}
                                        onChange={(e) => setFormData({ ...formData, folder_name: e.target.value })}
                                        placeholder="e.g., Invoice Photos - Downtown Branch"
                                    />
                                    <div className="text-xs text-muted-foreground mt-1">
                                        Optional human-readable name for the folder
                                    </div>
                                </Field>

                                <Field className="">
                                    <Label htmlFor="drive-id" className="">{t('gdrive.driveId', 'common')}</Label>
                                    <Input
                                        id="drive-id"
                                        type="text"
                                        value={formData.drive_id || ''}
                                        onChange={(e) => setFormData({ ...formData, drive_id: e.target.value })}
                                        placeholder="Leave empty for My Drive"
                                    />
                                    <div className="text-xs text-muted-foreground mt-1">
                                        Only needed for Shared Drives (Team Drives)
                                    </div>
                                </Field>
                            </div>
                        )}
                    </div>

                    {/* Advanced Settings - Collapsed by default */}
                    <div className="border-t pt-3">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                            className="text-xs w-full justify-start"
                        >
                            {showAdvancedSettings ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
                            {t('gdrive.advancedSettings', 'common')}
                        </Button>

                        {showAdvancedSettings && (
                            <div className="space-y-4 pt-3">
                                <Field className="">
                                    <Label htmlFor="processing-priority" className="">Processing Priority (1-10)</Label>
                                    <Input
                                        id="processing-priority"
                                        type="text"
                                        value={formData.processing_priority}
                                        onChange={(e) => setFormData({ ...formData, processing_priority: e.target.value })}
                                        placeholder="5"
                                    />
                                    <div className="text-xs text-muted-foreground mt-1">
                                        Higher numbers are processed first (1=lowest, 10=highest priority)
                                    </div>
                                </Field>

                                <Field className="">
                                    <Label htmlFor="sync-frequency" className="">Sync Frequency (hours)</Label>
                                    <Input
                                        id="sync-frequency"
                                        type="text"
                                        value={formData.sync_frequency_hours}
                                        onChange={(e) => setFormData({ ...formData, sync_frequency_hours: e.target.value })}
                                        placeholder="24"
                                    />
                                    <div className="text-xs text-muted-foreground mt-1">
                                        How often to automatically sync the folder (in hours)
                                    </div>
                                </Field>

                                <Field className="">
                                    <Label htmlFor="service-account-json" className="">Service Account JSON Key (Legacy)</Label>
                                    <textarea
                                        id="service-account-json"
                                        value={formData.service_account_json || ''}
                                        onChange={(e) => setFormData({ ...formData, service_account_json: e.target.value })}
                                        placeholder="Paste your Google Service Account JSON key here..."
                                        rows={6}
                                        className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 font-mono text-xs"
                                    />
                                    <div className="text-xs text-muted-foreground mt-1">
                                        Optional: For legacy setups without OAuth. Use "Connect with Google" above instead.
                                    </div>
                                </Field>
                            </div>
                        )}
                    </div>

                    {/* Folder Categorization Rules */}
                    <div className="space-y-3 pt-4 border-t">
                        <div className="flex items-start space-x-2">
                            <Info className="h-4 w-4 text-muted-foreground mt-0.5" />
                            <div className="flex-1">
                                <div className="text-sm font-semibold">File Categorization Rules</div>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Configure how files are categorized based on their folder location. Files in matching folders will be routed to different processing pipelines.
                                </p>
                            </div>
                        </div>

                        {/* Bank Statements */}
                        <div className="space-y-2 p-3 border rounded-lg bg-muted/10">
                            <div className="flex items-center justify-between">
                                <div className="text-sm font-medium">{t('gdrive.bankStatements', 'common')}</div>
                                <Badge variant="outline" className="text-xs">bank_statement</Badge>
                            </div>
                            <div className="space-y-2">
                                <Field className="">
                                    <Label htmlFor="bank-folder-names" className="text-xs">Folder Names (comma-separated)</Label>
                                    <Input
                                        id="bank-folder-names"
                                        type="text"
                                        value={rawInputs.bankFolderNames}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setRawInputs({ ...rawInputs, bankFolderNames: value });
                                            const names = value.split(',').map(s => s.trim()).filter(s => s);
                                            setFormData({
                                                ...formData,
                                                folder_category_rules: {
                                                    ...formData.folder_category_rules,
                                                    bank_statement: {
                                                        folder_names: names,
                                                        folder_keywords: formData.folder_category_rules?.bank_statement?.folder_keywords || [],
                                                        folder_ids: formData.folder_category_rules?.bank_statement?.folder_ids || []
                                                    }
                                                }
                                            });
                                        }}
                                        placeholder="e.g., Bank Statements, Banki kivonatok"
                                        className="text-sm"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Files in folders with these names will be treated as bank statements
                                    </p>
                                </Field>
                                <Field className="">
                                    <Label htmlFor="bank-folder-keywords" className="text-xs">Folder Keywords (comma-separated, ALL must match)</Label>
                                    <Input
                                        id="bank-folder-keywords"
                                        type="text"
                                        value={rawInputs.bankFolderKeywords}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setRawInputs({ ...rawInputs, bankFolderKeywords: value });
                                            const keywords = value.split(',').map(s => s.trim()).filter(s => s);
                                            setFormData({
                                                ...formData,
                                                folder_category_rules: {
                                                    ...formData.folder_category_rules,
                                                    bank_statement: {
                                                        folder_names: formData.folder_category_rules?.bank_statement?.folder_names || [],
                                                        folder_keywords: keywords,
                                                        folder_ids: formData.folder_category_rules?.bank_statement?.folder_ids || []
                                                    }
                                                }
                                            });
                                        }}
                                        placeholder="e.g., bank, kivonat"
                                        className="text-sm"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        ALL keywords must be present in folder path (e.g., "bank, kivonat" matches both "Bankkivonatok" and "Bankszámla kivonatok")
                                    </p>
                                </Field>
                                <Field className="">
                                    <Label htmlFor="bank-folder-ids" className="text-xs">Folder IDs (comma-separated)</Label>
                                    <Input
                                        id="bank-folder-ids"
                                        type="text"
                                        value={rawInputs.bankFolderIds}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setRawInputs({ ...rawInputs, bankFolderIds: value });
                                            const ids = value.split(',').map(s => s.trim()).filter(s => s);
                                            setFormData({
                                                ...formData,
                                                folder_category_rules: {
                                                    ...formData.folder_category_rules,
                                                    bank_statement: {
                                                        folder_names: formData.folder_category_rules?.bank_statement?.folder_names || [],
                                                        folder_keywords: formData.folder_category_rules?.bank_statement?.folder_keywords || [],
                                                        folder_ids: ids
                                                    }
                                                }
                                            });
                                        }}
                                        placeholder="e.g., 1a2b3c4d5e6f7g8h9i"
                                        className="text-sm"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Optional: Specific folder IDs for precise matching
                                    </p>
                                </Field>
                            </div>
                        </div>

                        {/* Payment Reports */}
                        <div className="space-y-2 p-3 border rounded-lg bg-muted/10">
                            <div className="flex items-center justify-between">
                                <div className="text-sm font-medium">{t('gdrive.paymentReports', 'common')}</div>
                                <Badge variant="outline" className="text-xs">payment_report</Badge>
                            </div>
                            <div className="space-y-2">
                                <Field className="">
                                    <Label htmlFor="payment-folder-names" className="text-xs">Folder Names (comma-separated)</Label>
                                    <Input
                                        id="payment-folder-names"
                                        type="text"
                                        value={rawInputs.paymentFolderNames}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setRawInputs({ ...rawInputs, paymentFolderNames: value });
                                            const names = value.split(',').map(s => s.trim()).filter(s => s);
                                            setFormData({
                                                ...formData,
                                                folder_category_rules: {
                                                    ...formData.folder_category_rules,
                                                    payment_report: {
                                                        folder_names: names,
                                                        folder_keywords: formData.folder_category_rules?.payment_report?.folder_keywords || [],
                                                        folder_ids: formData.folder_category_rules?.payment_report?.folder_ids || []
                                                    }
                                                }
                                            });
                                        }}
                                        placeholder="e.g., Payment Reports, Stripe, PayPal"
                                        className="text-sm"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Files in folders with these names will be treated as payment reports
                                    </p>
                                </Field>
                                <Field className="">
                                    <Label htmlFor="payment-folder-keywords" className="text-xs">Folder Keywords (comma-separated, ALL must match)</Label>
                                    <Input
                                        id="payment-folder-keywords"
                                        type="text"
                                        value={rawInputs.paymentFolderKeywords}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setRawInputs({ ...rawInputs, paymentFolderKeywords: value });
                                            const keywords = value.split(',').map(s => s.trim()).filter(s => s);
                                            setFormData({
                                                ...formData,
                                                folder_category_rules: {
                                                    ...formData.folder_category_rules,
                                                    payment_report: {
                                                        folder_names: formData.folder_category_rules?.payment_report?.folder_names || [],
                                                        folder_keywords: keywords,
                                                        folder_ids: formData.folder_category_rules?.payment_report?.folder_ids || []
                                                    }
                                                }
                                            });
                                        }}
                                        placeholder="e.g., payment, report"
                                        className="text-sm"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        ALL keywords must be present in folder path
                                    </p>
                                </Field>
                                <Field className="">
                                    <Label htmlFor="payment-folder-ids" className="text-xs">Folder IDs (comma-separated)</Label>
                                    <Input
                                        id="payment-folder-ids"
                                        type="text"
                                        value={rawInputs.paymentFolderIds}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setRawInputs({ ...rawInputs, paymentFolderIds: value });
                                            const ids = value.split(',').map(s => s.trim()).filter(s => s);
                                            setFormData({
                                                ...formData,
                                                folder_category_rules: {
                                                    ...formData.folder_category_rules,
                                                    payment_report: {
                                                        folder_names: formData.folder_category_rules?.payment_report?.folder_names || [],
                                                        folder_keywords: formData.folder_category_rules?.payment_report?.folder_keywords || [],
                                                        folder_ids: ids
                                                    }
                                                }
                                            });
                                        }}
                                        placeholder="e.g., 9i8h7g6f5e4d3c2b1a"
                                        className="text-sm"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Optional: Specific folder IDs for precise matching
                                    </p>
                                </Field>
                            </div>
                        </div>

                        <div className="flex items-start space-x-2 text-xs text-muted-foreground p-2 bg-blue-50 dark:bg-blue-950/20 rounded border border-blue-200 dark:border-blue-800">
                            <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                            <div>
                                {t('gdrive.howItWorks', 'common')}
                            </div>
                        </div>
                    </div>

                    <Field className="">
                        <Label className="">
                            <input
                                type="checkbox"
                                checked={formData.is_shared_folder}
                                onChange={(e) => setFormData({ ...formData, is_shared_folder: e.target.checked })}
                                className="mr-2"
                            />
                            {t('gdrive.sharedFolder', 'common')}
                        </Label>
                        <div className="text-xs text-muted-foreground mt-1">
                            Check this if multiple cost centers share the same Google Drive folder
                        </div>
                    </Field>

                    <Field className="">
                        <Label className="">
                            <input
                                type="checkbox"
                                checked={formData.sync_enabled}
                                onChange={(e) => setFormData({ ...formData, sync_enabled: e.target.checked })}
                                className="mr-2"
                            />
                            {t('gdrive.syncEnabled', 'common')}
                        </Label>
                    </Field>

                    <Field className="">
                        <Label className="">
                            <input
                                type="checkbox"
                                checked={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                className="mr-2"
                            />
                            {t('status.active', 'common')}
                        </Label>
                    </Field>

                    <div className="flex space-x-2 pt-2">
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? t('actions.saving', 'common') : (credentials ? t('actions.update', 'common') : t('actions.save', 'common'))}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowForm(false);
                                setFormData(prev => ({
                                    ...prev,
                                    folder_id: credentials?.folder_id || '',
                                    folder_name: credentials?.folder_name || '',
                                    is_shared_folder: credentials?.is_shared_folder || false,
                                    processing_priority: credentials?.processing_priority || '5',
                                    sync_enabled: credentials?.sync_enabled ?? true,
                                    sync_frequency_hours: credentials?.sync_frequency_hours || '24',
                                    is_active: credentials?.is_active ?? true,
                                    folder_category_rules: credentials?.folder_category_rules || {}
                                }));
                            }}
                        >
                            {t('actions.cancel', 'common')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
